import PropertySwitch from './components/PropertySwitch';
import PropertyRadio from './components/PropertyRadio';
import BaseFeature from './BaseFeature';
import FeatureCard from './components/FeatureCard';
import {cn} from 'lib/utils';
import {DEXAL_POWER_SUPPLY_UNIT} from '../../../../../../utils/constantFeatures';
import {getErrorIssues, getWarningIssues} from '../../../../../../utils/validationSummary';
import {Trans} from 'react-i18next';

export default class DexalPSU extends BaseFeature {

    constructor(props) {
        super(props);

        this.state = {
            isEnabled: this.props.properties[DEXAL_POWER_SUPPLY_UNIT.enable].value === "1" ? true : false
        };

        this.handleChange = this.handleChange.bind(this);
    }

    handleChange(propertyName, value) {

        const v = value;
        this.props.handleChange(propertyName, v);

        if (this.getCorrectPropertyName(propertyName) === DEXAL_POWER_SUPPLY_UNIT.enable) {
            this.setState({isEnabled: v === "1" ? true : false});
        }
    }

    // Same error/warning signal as BaseFeature.getClassName, minus the legacy
    // .card-feature-component base class — that one is `width: fit-content` and would
    // beat the modern card's `w-full` (both single-class specificity, and
    // stylesheet.css loads after Tailwind).
    cardState(properties) {
        if (getErrorIssues(this.props.issues, properties).length > 0) return 'danger';
        if (getWarningIssues(this.props.issues, properties).length > 0) return 'warning';
        return '';
    }

    render() {

        const enable = this.props.properties[DEXAL_POWER_SUPPLY_UNIT.enable];
        const mainsOffMode = this.props.properties[DEXAL_POWER_SUPPLY_UNIT.mainsOffMode];

        return (
            // The card rendered full width in built output. Cause was not this page: the
            // minifier collapsed `.card-feature-component`'s `width: fit-content;
            // width: -moz-max-content` pair down to the invalid -moz value, so the card
            // fell back to `width: auto`. Fixed at source in stylesheet.css. A block-level
            // card like this one had nothing else holding its width in — pages that wrap
            // their card in `.flex-row` were never affected, because a flex item on the
            // main axis is content-sized regardless.
            //
            // The modern grid below sets the width explicitly, so this page no longer
            // depends on that CSS either way.
            // The `<Col sm={12} …>` these cards used to sit in made it worse: a
            // full-width column left the switch ~1500px from its own label.
            //
            // PropertySwitch and PropertyRadio are deliberately still the legacy
            // components (they have 29 and 9 consumers respectively and no modern
            // counterpart yet), so the body stays a plain block rather than
            // bodyLayout="fields" — that grid expects FieldRow children, and these two
            // render their own internal Row/Col.
            <div className="mx-auto w-full max-w-[1800px] px-4 pb-4">
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 xl:grid-cols-12">

                    <FeatureCard
                        variant="modern"
                        title={<Trans i18nKey="Dexal_Power_Supply_Unit"/>}
                        className={cn('lg:col-span-1 xl:col-span-4', this.cardState([enable]))}>
                        <PropertySwitch
                            property={enable}
                            title={enable.info.displayName.replace(/([A-Z])/g, ' $1')}
                            handleChange={this.handleChange}/>
                    </FeatureCard>

                    {mainsOffMode && (
                        <FeatureCard
                            variant="modern"
                            title={<Trans i18nKey="Supply_On_Mains_Off"/>}
                            /* One column wider than the card beside it: these radio
                               labels carry a second explanatory line. */
                            className={cn('lg:col-span-2 xl:col-span-5', this.cardState([mainsOffMode]))}>
                            <PropertyRadio
                                disabled={!this.state.isEnabled}
                                property={mainsOffMode}
                                isChecked={mainsOffMode.value === "1"}
                                valueForTrue={["1"]}
                                title={
                                    <span>
                                        <Trans i18nKey="Maintain_Supply"/>
                                        <div style={{fontSize: '0.8em', color: '#6c757d'}}>
                                            <Trans i18nKey="Maintain_Supply_Subtitle"/>
                                        </div>
                                    </span>
                                }
                                handleChange={this.handleChange}/>
                            <PropertyRadio
                                disabled={!this.state.isEnabled}
                                property={mainsOffMode}
                                isChecked={mainsOffMode.value === "0"}
                                valueForTrue={["0"]}
                                title={<Trans i18nKey="Immediate_Off"/>}
                                handleChange={this.handleChange}/>
                        </FeatureCard>
                    )}
                </div>
            </div>
        );
    }
}
